import {
  addToast,
  Button,
  clearAllToasts,
  ErrorToast,
  Skeleton,
  SuccessToast,
} from '@otter-ai/den-components';
import { getDesktopAppUtmContent } from '@otter-ai/desktop';
import type { Plan } from '@otter-ai/recall-client';
import {
  CheckoutDialogImpressionEvent,
  CheckoutDialogInteractionEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useUpdateShippingAddress } from '@otter-ai/zone-purchase/src/lib/DO_NOT_USE';
import { useCheckout } from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import { addMonths } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import { useFormatter, useTranslations } from 'next-intl';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { NUM_MONTHS_IN_YEAR } from '@/constants/time';
import { useExperiment } from '@/experiment/experiments-client';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { userQueryCreator } from '@/query/userQueryCreator';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import { adhocRecordingSpeechOtidAtom } from '@/shared/atoms/speech/adhocRecording.atom';
import { checkoutTrackingAtom } from '@/shared/modals/cc-trial-modal/utils/atoms';
import { OtterPlanType } from '@/shared/types/plan';
import { useCompleteCheckout } from './hooks/useCompleteCheckout';
import { useUpdateCheckoutPlan } from './hooks/useUpdateCheckoutPlan';
import { PlanSelection } from './PlanSelection';
import { PricingSummary } from './PricingSummary';
import {
  getCheckoutAnalyticsData,
  isCheckoutDataReady,
} from './utils/checkoutAnalyticsUtils';
import { getPlanTitleFromPlan } from './utils/planTitle';
import { handlePurchaseSuccessRedirect } from './utils/purchaseRedirect';
import { extractLocationFromSettings, formatName } from './utils/utils';

export const CheckoutDialogForm = forwardRef<
  HTMLDivElement,
  {
    onCloseDialog?: () => void;
    selectedPlan: Plan | undefined;
    planOptions: {
      annualPlan?: Plan;
      monthlyPlan?: Plan;
    };
    setSelectedPlan: (plan: Plan) => void;
    numWorkspaceMembers: number;
    sessionIsUpdating: boolean;
    setSessionIsUpdating: (isUpdating: boolean) => void;
    submitButtonText: string;
    checkoutType: 'reactivate' | 'upgrade';
    purchaseInProgress: boolean;
    setPurchaseInProgress: (purchaseInProgress: boolean) => void;
  }
>(function CheckoutForm(
  {
    onCloseDialog,
    selectedPlan,
    planOptions,
    setSelectedPlan,
    numWorkspaceMembers,
    sessionIsUpdating,
    setSessionIsUpdating,
    submitButtonText,
    checkoutType,
    purchaseInProgress,
    setPurchaseInProgress,
  },
  ref,
) {
  const t = useTranslations('settings:billing');
  const router = useRouter();
  const adhocRecordingOtid = useAtomValue(adhocRecordingSpeechOtidAtom);
  const checkoutTracking = useAtomValue(checkoutTrackingAtom);
  const postPurchaseOnboardingExp = useExperiment(
    'post_purchase_onboarding_flow',
  );
  const { data: subscription } = useQuery(subscriptionQuery());
  const { data: workspace } = useQuery(workspaceQueryCreator());
  const { data: user } = useQuery(userQueryCreator());

  const updateCheckoutPlan = useUpdateCheckoutPlan({
    selectedPlan,
    setSelectedPlan,
    setSessionIsUpdating,
  });

  const formatter = useFormatter();

  const defaultPaymentMethod = subscription?.payment_methods?.find(
    (payment) => payment.is_default,
  );

  const updateShippingAddress = useUpdateShippingAddress({
    setIsLoading: setSessionIsUpdating,
  });

  const { handleCompleteCheckout, isGeneratingSubscription } =
    useCompleteCheckout({
      onCloseDialog,
      defaultPaymentMethodId: defaultPaymentMethod?.id,
      selectedPlan,
      checkoutType,
      setPurchaseInProgress,
      onError: (errorMessage: string) => {
        const toastMessage = errorMessage
          ? `${errorMessage} ${t('checkout.redirectingToPurchasePage')}`
          : t('checkout.redirectingToPurchasePage');

        addToast(<ErrorToast description={toastMessage} duration={2500} />);

        setTimeout(() => {
          clearAllToasts();
          const redirectMessage = t('checkout.pleaseTryAgain');

          router.push(
            `${getPurchasePageUrl(selectedPlan)}?f=billing_page&message=${encodeURIComponent(redirectMessage)}&h=${selectedPlan?.payment_period_type}`,
          );
        }, 2500);
      },
      onPurchaseComplete: () => {
        const billingCycle =
          selectedPlan?.payment_period_type === 'month'
            ? t('monthly')
            : t('annually');
        const planName = selectedPlan
          ? getPlanTitleFromPlan(selectedPlan, t)
          : '';
        const toastMessage =
          checkoutType === 'upgrade'
            ? t('planUpgradeSuccess')
            : t('youHaveReactivatedYourPlan', {
                planName,
                billingCycle,
              });
        addToast(<SuccessToast description={toastMessage} />);

        // Redirect to onboarding after successful purchase
        // If user is in a live recording, open onboarding in a new tab
        if (selectedPlan?.type) {
          const isRecording = !!adhocRecordingOtid;
          handlePurchaseSuccessRedirect(selectedPlan.type, {
            isRecording,
            experimentGroup: postPurchaseOnboardingExp.group,
          });
        }
      },
    });

  const location = extractLocationFromSettings(
    workspace?.workspace?.settings ?? user?.settings,
  );
  const name = formatName(
    user?.user?.first_name || '',
    workspace?.workspace?.name,
  );

  const isProcessingOrGeneratingSubscription =
    purchaseInProgress || isGeneratingSubscription;

  useEffect(() => {
    const updateAddress = () => {
      updateShippingAddress.mutate({
        country: location?.country || '',
        postal_code: location?.postal_code || '',
        name,
      });
    };
    updateAddress();
  }, [location?.country, location?.postal_code, name]);

  const { total, recurring, minorUnitsAmountDivisor, lineItems, tax } =
    useCheckout();
  const hasTrackedImpression = useRef(false);

  // Track impression event once when all data is available
  useEffect(() => {
    if (
      !hasTrackedImpression.current &&
      tax.status === 'ready' &&
      isCheckoutDataReady(total, lineItems, selectedPlan, sessionIsUpdating) &&
      selectedPlan
    ) {
      const analyticsData = getCheckoutAnalyticsData({
        total,
        lineItems,
        selectedPlan,
        minorUnitsAmountDivisor,
        checkoutType,
      });

      trackEvent(
        CheckoutDialogImpressionEvent({
          ...analyticsData,
          ...(checkoutTracking.premiumFeature && {
            PremiumFeature: checkoutTracking.premiumFeature,
          }),
          ...(checkoutTracking.checkoutEntryPoint && {
            CheckoutEntryPoint: checkoutTracking.checkoutEntryPoint,
          }),
        }),
      );
      hasTrackedImpression.current = true;
    }
  }, [
    total,
    lineItems,
    selectedPlan,
    minorUnitsAmountDivisor,
    checkoutType,
    sessionIsUpdating,
    checkoutTracking,
  ]);

  const renewalDate = useMemo(() => {
    if (!recurring) return null;
    let date = null;
    if (recurring.interval === 'year') {
      date = addMonths(new Date(), NUM_MONTHS_IN_YEAR);
    } else {
      date = addMonths(new Date(), 1);
    }
    return formatter.dateTime(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [recurring]);

  if (!selectedPlan) {
    return null;
  }

  const utmContent = getDesktopAppUtmContent();
  const payWithPurchaseHref = `${
    selectedPlan.type === OtterPlanType.PREMIUM
      ? PATH_URL.PURCHASE_PRO_PLAN
      : PATH_URL.PURCHASE_TEAM_PLAN
  }?utm_content=${encodeURIComponent(utmContent)}`;

  const onPlanChange = (plan: Plan) => {
    updateCheckoutPlan.mutate({ plan, seatCount: numWorkspaceMembers });
  };

  return (
    <div ref={ref} className="px-4 pb-4 sm:px-6 sm:pb-6">
      <div className="bg-palette-pink-250 h-[1px] mb-4 w-full" />
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h3 className="text-md font-semibold">
            {t('checkout.paymentMethod')}
          </h3>
          {defaultPaymentMethod && (
            <div className="text-sm sm:text-md font-semibold">
              {t('checkout.payWith')}{' '}
              <a
                href={payWithPurchaseHref}
                className="underline hover:text-palette-gray-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                {defaultPaymentMethod?.brand.charAt(0).toUpperCase() +
                  defaultPaymentMethod?.brand.slice(1)}{' '}
                {defaultPaymentMethod?.last4}
              </a>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between text-sm sm:text-md">
          <PlanSelection
            selectedPlan={selectedPlan}
            planOptions={planOptions}
            onPlanChange={onPlanChange}
            numSeats={numWorkspaceMembers}
          />
          <div className="max-h-4 sm:ml-4">
            {sessionIsUpdating ? (
              <Skeleton className="h-4 my-0" />
            ) : (
              total.subtotal.amount
            )}
          </div>
        </div>

        <PricingSummary
          sessionIsUpdating={sessionIsUpdating}
          total={total}
          recurring={recurring}
          renewalDate={renewalDate}
          minorUnitsAmountDivisor={minorUnitsAmountDivisor}
        />
      </div>
      <div className="flex flex-col-reverse gap-3 mt-6 sm:flex-row sm:justify-end sm:gap-4">
        {!isProcessingOrGeneratingSubscription && (
          <Button
            className="rounded-lg"
            variant="outlineNeutral"
            version="legacy"
            onClick={() => {
              trackEvent(
                CheckoutDialogInteractionEvent({
                  Action: 'cancel',
                }),
              );
              onCloseDialog?.();
            }}
          >
            {t('cancel')}
          </Button>
        )}
        <Button
          variant="primary"
          className="rounded-lg"
          disabled={isProcessingOrGeneratingSubscription || sessionIsUpdating}
          onClick={() => {
            // Track interaction event when user clicks confirm
            if (
              isCheckoutDataReady(
                total,
                lineItems,
                selectedPlan,
                sessionIsUpdating,
              ) &&
              selectedPlan
            ) {
              const analyticsData = getCheckoutAnalyticsData({
                total,
                lineItems,
                selectedPlan,
                minorUnitsAmountDivisor,
                checkoutType,
              });

              trackEvent(
                CheckoutDialogInteractionEvent({
                  ...analyticsData,
                  Action: 'confirmPurchase',
                }),
              );
            }
            handleCompleteCheckout();
          }}
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
});

function getPurchasePageUrl(selectedPlan?: Plan) {
  const utmContent = getDesktopAppUtmContent();
  const basePath =
    selectedPlan?.type === OtterPlanType.TEAM
      ? PATH_URL.PURCHASE_TEAM_PLAN
      : PATH_URL.PURCHASE_PRO_PLAN;
  return `${basePath}?utm_content=${encodeURIComponent(utmContent)}`;
}
